/**
 * GridContext - Provides global grid state management
 * Wraps useGrid hook in a context for component access
 */

import { createContext, useContext, ReactNode } from 'react';
import { useGrid, UseGridReturn } from '../hooks/useGrid';

/**
 * Grid context type
 */
type GridContextType = UseGridReturn;

/**
 * Grid context
 */
const GridContext = createContext<GridContextType | undefined>(undefined);

/**
 * Props for GridProvider
 */
interface GridProviderProps {
  /** Child components */
  children: ReactNode;
  /** Whether to auto-save to localStorage (default: true) */
  autoSave?: boolean;
}

/**
 * GridProvider component
 * Provides grid state and actions to all child components
 * 
 * @param props - Provider props
 * @returns Provider component
 */
export function GridProvider({ children, autoSave = true }: GridProviderProps) {
  const gridState = useGrid(autoSave);

  return (
    <GridContext.Provider value={gridState}>
      {children}
    </GridContext.Provider>
  );
}

/**
 * Hook to access grid context
 * Must be used within GridProvider
 * 
 * @returns Grid state and actions
 * @throws Error if used outside GridProvider
 */
export function useGridContext(): GridContextType {
  const context = useContext(GridContext);
  
  if (context === undefined) {
    throw new Error('useGridContext must be used within GridProvider');
  }
  
  return context;
}
